import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import MuiDialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import useUniqueId from '../hooks/useUniqueId';

const Dialog = ({ dialogState, size, dataAutomationId, ...muiDialogProps }) => {
    const [contentData, setContentData] = useState(undefined);
    const titleId = useUniqueId('dialog-title');
    const contentId = useUniqueId('dialog-content');

    const prevDialogStateRef = React.useRef(dialogState);
    useEffect(() => {
        const prevShowing = shouldShow(prevDialogStateRef.current);
        const nowShowing = shouldShow(dialogState);
        if (prevShowing && !nowShowing) {
            setContentData(undefined);
        }
        prevDialogStateRef.current = dialogState;
    }, [dialogState]);

    function shouldShow(ds) {
        if (!ds || ds.show === false) return false;
        return ds.controls && ds.controls.length > 0 && ds.content;
    }

    if (!shouldShow(dialogState)) return null;

    const heading = dialogState.heading;
    const content = React.cloneElement(dialogState.content, {
        gatherContentData: (data) => setContentData(data)
    });

    const escAction = dialogState.controls.filter(c => c.key === 'ESC');
    const closeAction = (escAction.length === 1 && escAction[0].action) || (() => {});
    const visibleControls = dialogState.controls.filter(c => c.label);

    const maxWidthMap = { L: 'lg', M: 'md', S: 'sm' };
    const maxWidth = maxWidthMap[size] || 'lg';

    return (
        <MuiDialog
            open={true}
            onClose={closeAction}
            maxWidth={maxWidth}
            fullWidth
            aria-labelledby={heading ? titleId : undefined}
            aria-describedby={contentId}
            {...muiDialogProps}
        >
            {heading && (
                <DialogTitle id={titleId} data-automation-id={dataAutomationId ? `${dataAutomationId}_title` : undefined}>
                    {heading}
                </DialogTitle>
            )}
            <DialogContent id={contentId} sx={{ overflow: 'auto', ...dialogState.overrideStyle }} {...dialogState.contentProps}>
                {content}
                {dialogState.contentItems}
            </DialogContent>
            {visibleControls.length > 0 &&
                <DialogActions>
                    {visibleControls.map((c, i) => {
                        const btnEnabled = c.validate ? c.validate(contentData) : true;
                        const controlSlug = (c.key || c.label || String(i)).toString().replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase();
                        return (
                            <Button
                                key={i}
                                disabled={!btnEnabled}
                                onClick={() => c.action(contentData)}
                                variant={c.variant || 'contained'}
                                size="small"
                                data-automation-id={dataAutomationId ? `${dataAutomationId}_${controlSlug}_btn` : undefined}
                            >
                                {c.label}
                            </Button>
                        );
                    })}
                </DialogActions>
            }
        </MuiDialog>
    );
};

Dialog.propTypes = {
    size: PropTypes.string,
    dialogState: PropTypes.object
};

export default Dialog;
